import React, { useCallback } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { LocationLink } from '../LocationLink'
import Button from '@mui/material/Button'
import { useData } from './hooks'
import Alert from '@mui/material/Alert'
import { useSelector } from 'react-redux'
import {
  selectEntityStatus,
  selectServiceLocationsDetails,
  setSelectedServiceLocationsDetails,
} from '@user/store/map/slice'
import Stack from '@mui/material/Stack'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'
import Link from '@mui/material/Link'
import LaunchIcon from '@mui/icons-material/Launch'
import { Theme } from '@mui/material/styles'
import ChevronRight from '@mui/icons-material/ChevronRight'
import { StatusSection } from './atoms/StatusSection'
import { ViewKnockStatusHistoryButton } from './atoms/ViewKnockStatusHistoryButton'
import { useD2DKnockEnabled } from '@user/store/tenantFeatures/hooks/useD2DKnockEnabled'
import { googleMapsOpenInMapsLink } from '@user/components/HereMap/utils/googleMapsLinks'
import { HereMapClickEvent } from '@user/components/HereMap/HereMapNew/hooks/useHereMapInstance'
import {
  ClearKnockStatusButton,
  ClearKnockStatusConfirmDialog,
  applyClearedOverride,
  useClearKnockStatus,
  useKnockStatusEditPermission,
} from '@user/features/MapView/ClearKnockStatus'
import { useAppDispatch } from '@user/store/hooks/useAppDispatch'
import { updateKnockStatusHistoryById } from '@user/store/knockStatus/api'
import { toast } from '@sales-center/components'
import { setEntityDetails } from '@user/store/map/slice'
import { buildKnockStatusReasonPatches } from '@user/utils/knockStatusReasonPatch'

type MapBubbleContentsLoadedProps = {
  onNameClick: (entityId: string, variant: string) => void
  onViewClick: (entityId: string, variant: string) => void
  onClose?: () => void
  showCloseBtn?: boolean
  onKnockStatusClick?: (entityId: string, variant: string) => void
  /** Re-fetches the map after the active knock status is cleared (SH-661). */
  onKnockStatusCleared?: () => void
  onViewKnockStatusHistory?: () => void
  knockStatusName?: string
  knockStatusReason?: string
}

function MapBubbleContentsLoaded({
  onNameClick,
  onViewClick,
  onClose,
  showCloseBtn,
  onKnockStatusClick,
  onKnockStatusCleared,
  onViewKnockStatusHistory,
  knockStatusName,
  knockStatusReason,
}: MapBubbleContentsLoadedProps) {
  const isD2dEnabled = useD2DKnockEnabled()
  const {
    addressDisplay,
    entityType,
    selectedEntity,
    serviceLocation,
    nameAndCompanyDisplay,
    entityTypeDisplay,
    formattedPhone,
    variant,
    status,
    knockStatus,
    canViewHistory,
  } = useData({
    data: {
      latitude: 0,
      longitude: 0,
      knockStatusReason: knockStatusReason,
      knockStatusSettingName: knockStatusName,
    } as HereMapClickEvent,
  })

  const entityStatus = useSelector(selectEntityStatus)
  const serviceLocationsDetails = useSelector(selectServiceLocationsDetails)
  const dispatch = useAppDispatch()
  const [isSavingReason, setIsSavingReason] = React.useState(false)

  const canView = entityStatus !== 'error'

  const [canEdit] = useKnockStatusEditPermission(variant ?? '')

  const knockStatusLocationId =
    serviceLocation?.knockStatusDetails?.knockStatusLocationId ?? null
  const hasKnockStatus =
    knockStatus.status !== '' &&
    knockStatus.status !== 'None' &&
    knockStatusLocationId !== null

  const {
    clearConfirmOpen,
    isClearing,
    cleared,
    openClearConfirm,
    closeClearConfirm,
    handleClearKnockStatus,
  } = useClearKnockStatus({
    knockStatusLocationId,
    variant: variant ?? '',
    onRefresh: onKnockStatusCleared,
  })

  const isDraftLead =
    variant?.toLowerCase() === 'draftlead' ||
    variant?.toLowerCase() === 'pendinglead'

  const handleSaveReason = useCallback(
    async (newReason: string | null) => {
      if (!knockStatus.id || !selectedEntity) return false
      setIsSavingReason(true)
      try {
        await dispatch(
          updateKnockStatusHistoryById({
            id: knockStatus.id,
            entityType,
            input: {
              customReason: newReason,
              knockStatusName: null,
              previousKnockStatusName: null,
              entityType,
              entityId: selectedEntity.id,
            },
          }),
        ).unwrap()

        // Patches BOTH the single-entity entityDetails slice AND the
        // multi-tenant selectedServiceLocationsDetails slice, so if a user
        // has this bubble and the multi-tenant list open at once for the
        // same record, both stay in sync live rather than one going stale
        // until its next refetch.
        if (serviceLocation) {
          const { patchedEntityDetails, patchedServiceLocationsDetails } =
            buildKnockStatusReasonPatches({
              entityId: selectedEntity.id,
              serviceLocationId: serviceLocation.id,
              isDraftLead,
              newReason,
              entityDetails: selectedEntity,
              serviceLocationsDetails: serviceLocationsDetails.locations,
              knockStatusLocationId:
                serviceLocation.knockStatusDetails?.knockStatusLocationId,
            })

          if (patchedEntityDetails) {
            dispatch(setEntityDetails(patchedEntityDetails))
          }
          if (patchedServiceLocationsDetails) {
            dispatch(
              setSelectedServiceLocationsDetails(
                patchedServiceLocationsDetails,
              ),
            )
          }
        }

        toast.success('Reason updated')
        return true
      } catch (e: any) {
        toast.error(e?.message || 'Failed to update reason')
        return false
      } finally {
        setIsSavingReason(false)
      }
    },
    [
      knockStatus.id,
      selectedEntity,
      serviceLocation,
      isDraftLead,
      entityType,
      serviceLocationsDetails,
      dispatch,
    ],
  )

  const onPhoneNumberClick = useCallback(() => {
    if (formattedPhone) {
      window.open(`tel:${formattedPhone}`, '_blank')
    }
  }, [formattedPhone])

  const onAddressMapsClick = useCallback(() => {
    if (!addressDisplay) {
      return
    }
    const url = googleMapsOpenInMapsLink(addressDisplay)
    window.open(url, '_blank', 'noopener,noreferrer')
  }, [addressDisplay])

  return (
    <>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="caption">{entityTypeDisplay}</Typography>
        {showCloseBtn && (
          <IconButton
            onClick={onClose}
            size={'small'}
            sx={{
              cursor: 'pointer',
              mr: '-10px',
            }}
            data-testid="action-modal-close-btn"
            data-test-id="mapBubbleLoadedCloseBtn"
          >
            <CloseIcon fontSize={'small'} />
          </IconButton>
        )}
      </Stack>
      <Link
        onClick={onNameClick?.bind(
          null,
          selectedEntity?.id?.toString() ?? '',
          variant ?? '',
        )}
        sx={{
          cursor: 'pointer',
          flexWrap: 'wrap',
          maxWidth: '300px',
          display: 'block',
        }}
      >
        <Typography variant="h6" sx={{ color: '#1565C0' }}>
          {nameAndCompanyDisplay}
        </Typography>
      </Link>
      {addressDisplay ? (
        <Link
          component="button"
          type="button"
          onClick={onAddressMapsClick}
          sx={{
            cursor: 'pointer',
            textAlign: 'left',
            border: 'none',
            background: 'none',
            padding: 0,
            mt: 0.4,
            display: 'inline-flex',
            alignItems: 'center',
            maxWidth: '100%',
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: '#1565C0',
              '&:hover': { color: 'rgba(25, 112, 211, 0.9)' },
            }}
          >
            {addressDisplay}
          </Typography>
          <LaunchIcon
            sx={{
              marginLeft: (theme: Theme) => theme.spacing(0.5),
              fontSize: '18px',
              color: '#1565C0',
              flexShrink: 0,
            }}
          />
        </Link>
      ) : null}
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        {formattedPhone ? (
          <Typography
            sx={{
              display: 'flex',
              alignItems: 'center',
              fontWeight: '500',
              alignContent: 'center',
            }}
            variant="subtitle4"
          >
            Phone:
            <Button
              color="primary"
              variant="text"
              size="small"
              onClick={onPhoneNumberClick}
              sx={{
                width: 'fit-content',
                color: '#1565C0',
              }}
              disableElevation
            >
              {formattedPhone}
            </Button>
          </Typography>
        ) : null}
        {serviceLocation?.locationCode && serviceLocation?.externalLinkId && (
          <LocationLink
            externalLocationId={serviceLocation.externalLinkId}
            externalLocationCode={serviceLocation.locationCode}
            billingLocation={false}
            typographyVariant="subtitle4"
            fontSize="14px"
            containerSx={{
              alignContent: 'center',
            }}
            linkButtonSx={{
              minWidth: 'fit-content',
              maxWidth: 'fit-content',
              padding: 0.5,
            }}
          />
        )}
      </Stack>
      <StatusSection
        status={status}
        knockStatus={applyClearedOverride(knockStatus, cleared)}
        chipMarginTop={Boolean(
          (!serviceLocation?.locationCode ||
            !serviceLocation?.externalLinkId) &&
            !formattedPhone,
        )}
        clearButton={
          // SH-1781: cleared hides it before the ~3s tile-refetch lag.
          isD2dEnabled && canView && hasKnockStatus && !cleared && canEdit ? (
            <ClearKnockStatusButton onClick={openClearConfirm} />
          ) : undefined
        }
        viewHistoryButton={
          // SH-1781: shows right after a clear; never hides earned history.
          (canViewHistory || cleared) &&
          onViewKnockStatusHistory && (
            <ViewKnockStatusHistoryButton onClick={onViewKnockStatusHistory} />
          )
        }
        canEdit={canEdit}
        isSavingReason={isSavingReason}
        onSaveReason={handleSaveReason}
      />
      <Box mt={1.6}>
        {canView ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
              gap: 0.8,
            }}
          >
            {isD2dEnabled && onKnockStatusClick ? (
              <Button
                fullWidth
                variant="contained"
                sx={{
                  backgroundColor: '#D4E3F3',
                }}
                onClick={onKnockStatusClick?.bind(
                  null,
                  selectedEntity?.id?.toString() ?? '',
                  variant ?? '',
                )}
                color="secondary"
              >
                Knock Status
              </Button>
            ) : null}
            {onViewClick ? (
              <Button
                fullWidth
                variant="contained"
                onClick={onViewClick?.bind(
                  null,
                  selectedEntity?.id?.toString() ?? '',
                  variant ?? '',
                )}
              >
                View
                <ChevronRight sx={{ fontSize: '20px', ml: '8px', mt: '1px' }} />
              </Button>
            ) : null}
          </Box>
        ) : (
          <Alert severity="warning">
            <Typography variant="body2">
              You don&apos;t have permission to view this record.
            </Typography>
          </Alert>
        )}
      </Box>

      <ClearKnockStatusConfirmDialog
        open={clearConfirmOpen}
        isClearing={isClearing}
        onCancel={closeClearConfirm}
        onConfirm={handleClearKnockStatus}
      />
    </>
  )
}

export default MapBubbleContentsLoaded
